import React from "react";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import DownloadIcon from "@mui/icons-material/Download";
import VisibilityIcon from "@mui/icons-material/Visibility";
import AddIcon from "@mui/icons-material/Add";
import SettingsIcon from "@mui/icons-material/Settings";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import BadgeIcon from "@mui/icons-material/Badge";
import ContactEmergencyIcon from "@mui/icons-material/ContactEmergency";
import DescriptionIcon from "@mui/icons-material/Description";
import ImageIcon from "@mui/icons-material/Image";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import Tooltip from "@mui/material/Tooltip";
import TextField from "@mui/material/TextField";
import IconButton from "@mui/material/IconButton";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import { Lightbox } from "react-modal-image";
import { objectToCSV, downloadCSV } from "../../../utilities/csvExport";
import { getRightworkAPI } from "../requests/utilities/requests";
import {
  API_get_certificates_for_store,
  API_get_certificate_types,
} from "../../../constants";
import LoadSpinner from "../../../utilities/LoadSpinner";
import { formatName } from "../../../helpers/helpers";
import CertificateDrawer from "../requests/shared/helpers/CertificateDrawer";
import Drawer from "@mui/material/Drawer";
import DrawerHeader from "../requests/shared/helpers/DrawerHeader";
import DrawerFooterContainerLarge from "../requests/shared/helpers/DrawerFooterContainerLarge";
import LoadingButton from "@mui/lab/LoadingButton";
import SaveIcon from "@mui/icons-material/Save";
import ButtonOutlined from "../requests/shared/helpers/ButtonOutlined";
import {
  CERTIFICATE_TYPE_OPTIONS,
  buildCertificateTypeLabels,
  getCustomTypeColor,
} from "../../../utilities/certificateTypes";
import ManageCertificateTypesModal from "./ManageCertificateTypesModal";

const STATUS_OPTIONS = [
  { value: "missing", label: "Missing" },
  { value: "expired", label: "Expired" },
  { value: "expiring_soon", label: "Expiring Soon" },
  { value: "valid", label: "Valid" },
  { value: "no_expiration", label: "No Expiration" },
];

const selectSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  height: "40px",
  width: "240px",
  fontSize: "14px",
  "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
    borderColor: "#6d28d9",
  },
};

const checkboxSx = {
  color: "#6d28d9",
  "&.Mui-checked": { color: "#6d28d9" },
  "&.MuiCheckbox-indeterminate": { color: "#6d28d9" },
};

function isPdf(url) {
  if (!url) return false;
  // Strip query params before checking extension
  const path = url.split("?")[0].toLowerCase();
  return path.endsWith(".pdf");
}

function CertificationsTable(props) {
  const [certificates, setCertificates] = React.useState([]);
  const [certificateTypes, setCertificateTypes] = React.useState([]);
  const [hiddenCertificateTypes, setHiddenCertificateTypes] = React.useState([]);
  const [allSystemTypes, setAllSystemTypes] = React.useState([]);
  const [requiredCertificateSlugs, setRequiredCertificateSlugs] = React.useState([]);
  const [manageTypesOpen, setManageTypesOpen] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [typeFilter, setTypeFilter] = React.useState([]);
  const [statusFilter, setStatusFilter] = React.useState([]);
  const [employeeFilter, setEmployeeFilter] = React.useState([]);
  const [showLightbox, setShowLightbox] = React.useState(false);
  const [selectedImage, setSelectedImage] = React.useState(null);
  const [drawerOpen, setDrawerOpen] = React.useState(false);
  const [drawerData, setDrawerData] = React.useState(null);
  const [drawerIsEdit, setDrawerIsEdit] = React.useState(false);
  const [sortColumn, setSortColumn] = React.useState(null);
  const [sortDirection, setSortDirection] = React.useState("asc");

  // Emergency contact drawer state
  const [ecDrawerOpen, setEcDrawerOpen] = React.useState(false);
  const [ecDrawerData, setEcDrawerData] = React.useState({
    store_team_member_id: null,
    employee_name: "",
    emergency_contact_name: "",
    emergency_contact_phone: "",
    emergency_contact_relationship: "",
  });
  const [ecSaving, setEcSaving] = React.useState(false);

  React.useEffect(() => {
    fetchCertificates();
    fetchCertificateTypes();
  }, [props.homeStore]);

  const fetchCertificateTypes = async () => {
    if (!props.homeStore) return;
    try {
      const res = await getRightworkAPI(API_get_certificate_types, {
        store_id: props.homeStore,
      });
      if (res.status === 200) {
        const payload = res.data?.redux?.payload || {};
        setCertificateTypes(payload.certificate_types || []);
        // Authoritative hidden-slug list, independent of the store-filtered catalog
        // (a hidden slug like break_waiver can be omitted from the catalog when its
        // store flag is off, so we can't rely on the catalog's overview_hidden flag).
        setHiddenCertificateTypes(payload.hidden_certificate_types || []);
        // Unfiltered built-in list for the Manage modal, so flag-gated types stay
        // toggleable (un-hideable) even in stores where their flag is off.
        setAllSystemTypes(payload.all_system_types || []);
        // Slugs required by any role — used to block hiding a required type.
        setRequiredCertificateSlugs(payload.required_certificate_types || []);
      }
    } catch (err) {
      // Non-fatal: fall back to the bundled default type list.
      console.error("Failed to fetch certificate types:", err);
    }
  };

  // Catalog mutations (archive / toggle expiration) also change dependent data on the
  // backend: archiving strips the slug from role requirements, and disabling
  // expiration clears matching Certificate dates. Refresh everything the table reads
  // (types, certificates, and the parent's employees/roles) so statuses and missing
  // rows don't stay stale until a full reload.
  const handleCatalogChanged = async () => {
    await fetchCertificateTypes();
    await fetchCertificates();
    if (props.refreshDependentData) {
      await props.refreshDependentData();
    }
  };

  // Role-requirement changes affect the catalog's required-slug set and the overview's
  // missing rows (driven by roles), but NOT the certificate rows — so skip
  // fetchCertificates (and its full-table spinner). The modal shows its own inline
  // loading indicator during this refresh.
  const handleRequirementsChanged = async () => {
    await fetchCertificateTypes();
    if (props.refreshDependentData) {
      await props.refreshDependentData();
    }
  };

  const fetchCertificates = async () => {
    if (!props.homeStore) return;

    setIsLoading(true);
    setError(null);

    try {
      const res = await getRightworkAPI(API_get_certificates_for_store, {
        store_id: props.homeStore,
      });

      if (res.status === 200) {
        const data = res.data?.redux?.payload?.certificates || [];
        setCertificates(data);
      }
    } catch (err) {
      console.error("Failed to fetch certificates:", err);
      setError("Failed to load certificates. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Types the company has hidden from this Certificates overview (display-only). These
  // are excluded from the type filter, the rows, the missing/expired counts, and the
  // add picker — but remain fully available on employee profiles.
  const overviewHiddenSlugs = React.useMemo(
    () =>
      new Set([
        // Authoritative company list (covers slugs omitted from the store catalog)...
        ...hiddenCertificateTypes,
        // ...plus any catalog entries flagged hidden (belt-and-suspenders).
        ...certificateTypes.filter((t) => t.overview_hidden).map((t) => t.value),
      ]),
    [hiddenCertificateTypes, certificateTypes]
  );

  // Filter options: use the fetched catalog when available, else bundled defaults.
  const typeOptions = React.useMemo(
    () =>
      certificateTypes.length > 0
        ? certificateTypes
            .filter((t) => !t.overview_hidden)
            .map((t) => ({ value: t.value, label: t.label }))
        : CERTIFICATE_TYPE_OPTIONS,
    [certificateTypes]
  );

  const certificateTypeLookup = React.useMemo(
    () => buildCertificateTypeLabels(certificateTypes),
    [certificateTypes]
  );

  const getCertificateIcon = (type) => {
    switch (type) {
      case "food_permit":
      case "liquor_permit":
        return BadgeIcon;
      case "emergency_contact":
        return ContactEmergencyIcon;
      default:
        return DescriptionIcon;
    }
  };

  const getCertificateTypeColor = (type) => {
    switch (type) {
      case "food_permit":
        return { bgColor: "bg-blue-100", iconColor: "text-blue-600" };
      case "liquor_permit":
        return { bgColor: "bg-purple-100", iconColor: "text-purple-600" };
      case "break_waiver":
        return { bgColor: "bg-amber-100", iconColor: "text-amber-600" };
      case "emergency_contact":
        return { bgColor: "bg-rose-100", iconColor: "text-rose-600" };
      case "other":
        return { bgColor: "bg-gray-100", iconColor: "text-gray-600" };
      default:
        // Custom company types: stable hashed color from the shared palette.
        return getCustomTypeColor(type);
    }
  };

  const getExpirationStatusKey = (expirationDate) => {
    if (!expirationDate) return "no_expiration";

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const expiry = new Date(expirationDate + "T00:00:00");
    const daysUntilExpiry = Math.round(
      (expiry - today) / (1000 * 60 * 60 * 24)
    );

    if (daysUntilExpiry < 0) return "expired";
    if (daysUntilExpiry <= 30) return "expiring_soon";
    return "valid";
  };

  const getExpirationStatus = (expirationDate, isMissing, cert) => {
    if (isMissing) {
      return { label: "Missing", color: "text-rose-700", bgColor: "bg-rose-100" };
    }
    if (cert?.isEmergencyContact || cert?.type === "emergency_contact") {
      return { label: "No Expiration", color: "text-gray-500", bgColor: "bg-gray-100" };
    }
    const key = getExpirationStatusKey(expirationDate);
    switch (key) {
      case "expired":
        return { label: "Expired", color: "text-red-700", bgColor: "bg-red-100" };
      case "expiring_soon":
        return { label: "Expiring Soon", color: "text-amber-700", bgColor: "bg-amber-100" };
      case "valid":
        return { label: "Valid", color: "text-green-700", bgColor: "bg-green-100" };
      default:
        return { label: "No Expiration", color: "text-gray-500", bgColor: "bg-gray-100" };
    }
  };

  const formatExpirationDate = (dateStr) => {
    if (!dateStr) return "—";
    const date = new Date(dateStr + "T00:00:00");
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const getInitials = (first, last) => {
    return `${(first || "")[0] || ""}${(last || "")[0] || ""}`.toUpperCase();
  };

  const handleMultiSelectChange = (setter, allOptions) => (event) => {
    const { value } = event.target;
    if (value.includes("all")) {
      setter((prev) =>
        prev.length === 0 ? allOptions.map((o) => o.value) : []
      );
    } else {
      setter(value);
    }
  };

  const renderMultiSelectValue = (selected, allOptions, allLabel) => {
    if (selected.length === 0) return allLabel;
    const labels = allOptions
      .filter((o) => selected.includes(o.value))
      .map((o) => o.label);
    if (labels.length <= 2) return labels.join(", ");
    return `${labels[0]}, +${labels.length - 1} more`;
  };

  const handleThumbnailClick = (e, fileUrl) => {
    e.stopPropagation();
    if (!fileUrl) return;
    if (isPdf(fileUrl)) {
      window.open(fileUrl, "_blank");
    } else {
      setSelectedImage(fileUrl);
      setShowLightbox(true);
    }
  };

  const handleRowClick = (cert) => {
    // Emergency contact rows (missing or valid) open the emergency contact drawer
    if (cert.type === "emergency_contact" && (cert.isMissing || cert.isEmergencyContact)) {
      setEcDrawerData({
        store_team_member_id: cert.store_team_member_id,
        employee_name: `${cert.employee_first_name} ${cert.employee_last_name}`,
        emergency_contact_name: cert.emergency_contact_name || "",
        emergency_contact_phone: cert.emergency_contact_phone || "",
        emergency_contact_relationship: cert.emergency_contact_relationship || "",
      });
      setEcDrawerOpen(true);
      return;
    }
    if (cert.isMissing) {
      // Open drawer in create mode, pre-filled with employee and type

      setDrawerData({
        uuid: null,
        type: cert.type,
        expiration_date: "",
        file: "",
        store_team_member_id: cert.store_team_member_id,
        prefillEmployeeId: cert.store_team_member_id,
      });
      setDrawerIsEdit(false);
      setDrawerOpen(true);
      return;
    }
    setDrawerData({
      uuid: cert.uuid,
      type: cert.type,
      expiration_date: cert.expiration_date || "",
      file: cert.file || "",
      store_team_member_id: cert.store_team_member_id,
    });
    setDrawerIsEdit(true);
    setDrawerOpen(true);
  };

  const handleAddClick = () => {
    setDrawerData(null);
    setDrawerIsEdit(false);
    setDrawerOpen(true);
  };

  const handleDrawerSave = async (certificateUuid, type, expirationDate, file, employeeId, clearFile) => {
    // For new certs, employeeId comes from the employee selector or prefill
    // For edits, use the store_team_member_id from the cert data
    const stmId = drawerIsEdit
      ? drawerData?.store_team_member_id
      : (employeeId || drawerData?.prefillEmployeeId);
    if (props.post_certificate && stmId) {
      await props.post_certificate(stmId, certificateUuid, type, expirationDate, file, clearFile);
      fetchCertificates();
    }
  };

  const handleDrawerDelete = async (certificateUuid) => {
    if (props.delete_certificate && drawerData?.store_team_member_id) {
      await props.delete_certificate(drawerData.store_team_member_id, certificateUuid);
      fetchCertificates();
    }
  };

  // Build unique employee options from certificates + visible employees
  const employeeOptions = React.useMemo(() => {
    const seen = new Set();
    const options = [];
    // From existing certificates
    certificates.forEach((cert) => {
      if (!seen.has(cert.store_team_member_id)) {
        seen.add(cert.store_team_member_id);
        options.push({
          value: cert.store_team_member_id,
          label: formatName(
            [cert.employee_first_name, cert.employee_last_name].filter(Boolean).join(" ")
          ),
        });
      }
    });
    // From visible employees (catches employees with zero certs)
    const employees = props.employees || [];
    employees.forEach((emp) => {
      if (!emp.is_hidden && !seen.has(emp.id)) {
        seen.add(emp.id);
        options.push({
          value: emp.id,
          label: formatName(
            [emp.first_name, emp.last_name].filter(Boolean).join(" ")
          ),
        });
      }
    });
    options.sort((a, b) => a.label.localeCompare(b.label));
    return options;
  }, [certificates, props.employees]);

  // Build synthetic "missing" rows for non-hidden employees missing required cert types
  const missingCertificates = React.useMemo(() => {
    const employees = props.employees || [];
    const visibleEmployees = employees.filter((emp) => !emp.is_hidden);
    const roleNames = props.roleNames || [];
    const roleRequirementLookup = roleNames.reduce((acc, role) => {
      acc[role.id] = role.required_certificate_types || [];
      return acc;
    }, {});

    // Build a set of "stmId:type" keys for existing certificates
    const existingKeys = new Set();
    certificates.forEach((cert) => {
      existingKeys.add(`${cert.store_team_member_id}:${cert.type}`);
    });

    const missing = [];
    visibleEmployees.forEach((emp) => {
      const requiredCertTypes = new Set();
      (emp.roles_and_role_types || []).forEach((roleCapability) => {
        const roleId = roleCapability?.scheduled_role;
        (roleRequirementLookup[roleId] || []).forEach((requiredType) => {
          requiredCertTypes.add(requiredType);
        });
      });

      Array.from(requiredCertTypes).forEach((type) => {
        // Skip required types the company has hidden from this overview.
        if (overviewHiddenSlugs.has(type)) return;
        if (!existingKeys.has(`${emp.id}:${type}`)) {
          missing.push({
            uuid: `missing-${emp.id}-${type}`,
            store_team_member_id: emp.id,
            employee_first_name: emp.first_name,
            employee_last_name: emp.last_name,
            type: type,
            expiration_date: null,
            file: null,
            isMissing: true,
          });
        }
      });
      // SB 294: Emergency contact rows for all visible employees (unless hidden)
      if (overviewHiddenSlugs.has("emergency_contact")) {
        // Emergency contact hidden from this overview; skip its rows.
      } else if (emp.emergency_contact_name) {
        missing.push({
          uuid: `ec-${emp.id}-emergency_contact`,
          store_team_member_id: emp.id,
          employee_first_name: emp.first_name,
          employee_last_name: emp.last_name,
          type: "emergency_contact",
          expiration_date: null,
          file: null,
          isMissing: false,
          isEmergencyContact: true,
          emergency_contact_name: emp.emergency_contact_name,
          emergency_contact_phone: emp.emergency_contact_phone,
          emergency_contact_relationship: emp.emergency_contact_relationship,
        });
      } else {
        missing.push({
          uuid: `missing-${emp.id}-emergency_contact`,
          store_team_member_id: emp.id,
          employee_first_name: emp.first_name,
          employee_last_name: emp.last_name,
          type: "emergency_contact",
          expiration_date: null,
          file: null,
          isMissing: true,
        });
      }
    });

    return missing;
  }, [props.employees, props.roleNames, certificates, overviewHiddenSlugs]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
  };

  const STATUS_ORDER = { missing: 0, expired: 1, expiring_soon: 2, valid: 3, no_expiration: 4 };

  const getRowStatusKey = (cert) => {
    if (cert.isMissing) return "missing";
    if (cert.isEmergencyContact) return "no_expiration";
    return getExpirationStatusKey(cert.expiration_date);
  };

  const filteredCertificates = React.useMemo(() => {
    // Determine whether to include missing rows
    const includeMissing =
      statusFilter.length === 0 || statusFilter.includes("missing");
    const includeExisting =
      statusFilter.length === 0 ||
      statusFilter.some((s) => s !== "missing");

    let result = [];

    // Add existing certificates (filtered by non-missing statuses). Exclude any whose
    // type the company has hidden from this overview (still shown on employee profiles).
    if (includeExisting) {
      let existing = certificates.filter(
        (cert) => !overviewHiddenSlugs.has(cert.type)
      );
      if (statusFilter.length > 0) {
        const nonMissingStatuses = statusFilter.filter((s) => s !== "missing");
        if (nonMissingStatuses.length > 0) {
          existing = existing.filter((cert) =>
            nonMissingStatuses.includes(getExpirationStatusKey(cert.expiration_date))
          );
        } else {
          existing = [];
        }
      }
      result = [...existing];
    }

    // Add synthetic rows (missing certs + emergency contact rows)
    missingCertificates.forEach((cert) => {
      const rowStatus = getRowStatusKey(cert);
      if (statusFilter.length === 0) {
        result.push(cert);
      } else if (statusFilter.includes(rowStatus)) {
        result.push(cert);
      }
    });

    // Employee filter — empty means show all (no filter active)
    if (employeeFilter.length > 0) {
      result = result.filter((cert) =>
        employeeFilter.includes(cert.store_team_member_id)
      );
    }

    // Type filter — empty means show all (no filter active)
    if (typeFilter.length > 0) {
      result = result.filter((cert) => typeFilter.includes(cert.type));
    }

    // Search filter
    const normalizedSearch = searchInput.trim().toLowerCase();
    if (normalizedSearch) {
      result = result.filter((cert) => {
        const name =
          `${cert.employee_first_name} ${cert.employee_last_name}`.toLowerCase();
        const type = (certificateTypeLookup[cert.type] || "").toLowerCase();
        return (
          name.includes(normalizedSearch) || type.includes(normalizedSearch)
        );
      });
    }

    // Default sort: type → status → expiration date
    if (!sortColumn) {
      result = [...result].sort((a, b) => {
        // 1. Type
        const typeA = (certificateTypeLookup[a.type] || "").toLowerCase();
        const typeB = (certificateTypeLookup[b.type] || "").toLowerCase();
        const typeCmp = typeB.localeCompare(typeA);
        if (typeCmp !== 0) return typeCmp;

        // 2. Status
        const statusA = STATUS_ORDER[getRowStatusKey(a)] ?? 99;
        const statusB = STATUS_ORDER[getRowStatusKey(b)] ?? 99;
        const statusCmp = statusA - statusB;
        if (statusCmp !== 0) return statusCmp;

        // 3. Expiration date
        const dateA = a.expiration_date || "";
        const dateB = b.expiration_date || "";
        if (!dateA && !dateB) return 0;
        if (!dateA) return 1;
        if (!dateB) return -1;
        return dateA.localeCompare(dateB);
      });
    }

    // Sort by selected column
    if (sortColumn) {
      result = [...result].sort((a, b) => {
        let cmp = 0;
        switch (sortColumn) {
          case "employee": {
            const nameA = `${a.employee_first_name} ${a.employee_last_name}`.toLowerCase();
            const nameB = `${b.employee_first_name} ${b.employee_last_name}`.toLowerCase();
            cmp = nameA.localeCompare(nameB);
            break;
          }
          case "type": {
            const typeA = (certificateTypeLookup[a.type] || "").toLowerCase();
            const typeB = (certificateTypeLookup[b.type] || "").toLowerCase();
            cmp = typeA.localeCompare(typeB);
            break;
          }
          case "expiration_date": {
            const dateA = a.expiration_date || "";
            const dateB = b.expiration_date || "";
            if (!dateA && !dateB) cmp = 0;
            else if (!dateA) cmp = 1;
            else if (!dateB) cmp = -1;
            else cmp = dateA.localeCompare(dateB);
            break;
          }
          case "status": {
            const statusA = STATUS_ORDER[getRowStatusKey(a)] ?? 99;
            const statusB = STATUS_ORDER[getRowStatusKey(b)] ?? 99;
            cmp = statusA - statusB;
            break;
          }
          default:
            break;
        }
        return sortDirection === "asc" ? cmp : -cmp;
      });
    }

    return result;
  }, [certificates, missingCertificates, employeeFilter, typeFilter, statusFilter, searchInput, sortColumn, sortDirection, certificateTypeLookup, overviewHiddenSlugs]);

  const handleEcChange = (evt) => {
    const { name, value } = evt.target;
    setEcDrawerData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEcSave = async () => {
    if (!props.onSaveEmergencyContact) return;
    setEcSaving(true);
    try {
      await props.onSaveEmergencyContact(
        ecDrawerData.store_team_member_id,
        ecDrawerData.emergency_contact_name,
        ecDrawerData.emergency_contact_phone,
        ecDrawerData.emergency_contact_relationship
      );
      setEcDrawerOpen(false);
    } catch (err) {
      console.error("Error saving emergency contact", err);
    } finally {
      setEcSaving(false);
    }
  };

  const exportCSV = () => {
    if (filteredCertificates.length === 0) return;

    const rows = filteredCertificates.map((cert) => ({
      "Employee": formatName(
        [cert.employee_first_name, cert.employee_last_name].filter(Boolean).join(" ")
      ),
      "Type": certificateTypeLookup[cert.type] || cert.type,
      "Expiration Date": cert.expiration_date || "N/A",
      "Status": getExpirationStatus(cert.expiration_date, cert.isMissing, cert).label,
    }));

    const csv = objectToCSV(rows);
    downloadCSV(csv, `certificates_overview.csv`);
  };

  if (isLoading) {
    return (
      <div className="px-6 py-8">
        <div className="flex items-center justify-center min-h-[400px]">
          <LoadSpinner />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8">
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center">
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Count stats from filtered results
  const filteredMissingCount = filteredCertificates.filter(
    (c) => c.isMissing
  ).length;
  const filteredExpiredCount = filteredCertificates.filter(
    (c) => !c.isMissing && getExpirationStatusKey(c.expiration_date) === "expired"
  ).length;
  const filteredExpiringSoonCount = filteredCertificates.filter(
    (c) => !c.isMissing && getExpirationStatusKey(c.expiration_date) === "expiring_soon"
  ).length;

  return (
    <div className="px-6 py-8">
      {showLightbox && selectedImage && (
        <div className="absolute w-screen h-screen z-40">
          <Lightbox
            small={selectedImage}
            medium={selectedImage}
            hideDownload={true}
            hideZoom={true}
            onClose={() => setShowLightbox(false)}
          />
        </div>
      )}

      {/* Title row with add button */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 text-sm text-slate-600">
            <BadgeIcon style={{ fontSize: "18px" }} />
            <span>{filteredCertificates.length} Certificates</span>
          </div>
          {filteredMissingCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-rose-50 border border-rose-200 rounded-full">
              <div className="w-1.5 h-1.5 bg-rose-500 rounded-full"></div>
              <span className="text-xs font-medium text-rose-700">
                {filteredMissingCount} Missing
              </span>
            </div>
          )}
          {filteredExpiredCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-full">
              <div className="w-1.5 h-1.5 bg-red-500 rounded-full"></div>
              <span className="text-xs font-medium text-red-700">
                {filteredExpiredCount} Expired
              </span>
            </div>
          )}
          {filteredExpiringSoonCount > 0 && (
            <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-full">
              <div className="w-1.5 h-1.5 bg-amber-500 rounded-full"></div>
              <span className="text-xs font-medium text-amber-700">
                {filteredExpiringSoonCount} Expiring Soon
              </span>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          <Tooltip title="Manage document types">
            <div
              className="h-10 px-3 rounded-full border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 flex items-center gap-1.5 cursor-pointer"
              onClick={() => setManageTypesOpen(true)}
            >
              <SettingsIcon style={{ fontSize: "18px" }} />
              <span className="text-sm font-medium">Manage Types</span>
            </div>
          </Tooltip>
          <Tooltip title="Add document">
            <div
              className="h-10 w-10 rounded-full bg-violet-700 hover:bg-violet-800 text-white flex items-center justify-center cursor-pointer"
              onClick={handleAddClick}
            >
              <AddIcon style={{ fontSize: "20px" }} />
            </div>
          </Tooltip>
        </div>
      </div>

      {/* Filters row */}
      <div className="flex items-center gap-3 mb-4">
          {/* Employee filter */}
          <Select
            multiple
            value={employeeFilter}
            onChange={handleMultiSelectChange(setEmployeeFilter, employeeOptions)}
            renderValue={(selected) =>
              renderMultiSelectValue(selected, employeeOptions, "All Employees")
            }
            size="small"
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="all">
              <Checkbox
                checked={employeeFilter.length === 0}
                indeterminate={
                  employeeFilter.length > 0 &&
                  employeeFilter.length < employeeOptions.length
                }
                sx={checkboxSx}
              />
              <ListItemText
                primary="All Employees"
                primaryTypographyProps={{ style: { fontWeight: "bold" } }}
              />
            </MenuItem>
            {employeeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox
                  checked={employeeFilter.includes(opt.value)}
                  sx={checkboxSx}
                />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
          {/* Type filter */}
          <Select
            multiple
            value={typeFilter}
            onChange={handleMultiSelectChange(setTypeFilter, typeOptions)}
            renderValue={(selected) =>
              renderMultiSelectValue(selected, typeOptions, "All Types")
            }
            size="small"
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="all">
              <Checkbox
                checked={typeFilter.length === 0}
                indeterminate={
                  typeFilter.length > 0 &&
                  typeFilter.length < typeOptions.length
                }
                sx={checkboxSx}
              />
              <ListItemText
                primary="All Types"
                primaryTypographyProps={{ style: { fontWeight: "bold" } }}
              />
            </MenuItem>
            {typeOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox
                  checked={typeFilter.includes(opt.value)}
                  sx={checkboxSx}
                />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
          {/* Status filter */}
          <Select
            multiple
            value={statusFilter}
            onChange={handleMultiSelectChange(setStatusFilter, STATUS_OPTIONS)}
            renderValue={(selected) =>
              renderMultiSelectValue(selected, STATUS_OPTIONS, "All Statuses")
            }
            size="small"
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="all">
              <Checkbox
                checked={statusFilter.length === 0}
                indeterminate={
                  statusFilter.length > 0 &&
                  statusFilter.length < STATUS_OPTIONS.length
                }
                sx={checkboxSx}
              />
              <ListItemText
                primary="All Statuses"
                primaryTypographyProps={{ style: { fontWeight: "bold" } }}
              />
            </MenuItem>
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                <Checkbox
                  checked={statusFilter.includes(opt.value)}
                  sx={checkboxSx}
                />
                <ListItemText primary={opt.label} />
              </MenuItem>
            ))}
          </Select>
          {/* Search */}
          <div className="flex-1 min-w-[280px]">
            <TextField
              fullWidth
              placeholder="Search by name or type..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <SearchIcon
                    sx={{ mr: 1, color: "text.secondary", fontSize: 20 }}
                  />
                ),
                endAdornment: searchInput && (
                  <IconButton
                    size="small"
                    onClick={() => setSearchInput("")}
                  >
                    <ClearIcon fontSize="small" />
                  </IconButton>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  height: "40px",
                  backgroundColor: "white",
                  borderRadius: "8px",
                  fontSize: "14px",
                  "&.Mui-focused fieldset": {
                    borderColor: "#6d28d9",
                  },
                },
              }}
            />
          </div>
          {/* Export */}
          <button
            className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 h-[40px] rounded-lg flex items-center gap-2 transition-colors shadow-sm whitespace-nowrap"
            onClick={exportCSV}
          >
            <DownloadIcon style={{ fontSize: "20px" }} />
            <span className="font-medium">Export CSV</span>
          </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
        {filteredCertificates.length > 0 ? (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-white">
                <tr>
                  {[
                    { key: "employee", label: "Employee" },
                    { key: "type", label: "Type" },
                    { key: "expiration_date", label: "Expiration Date" },
                    { key: "status", label: "Status" },
                  ].map((col) => (
                    <th
                      key={col.key}
                      className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer select-none hover:text-gray-700"
                      onClick={() => handleSort(col.key)}
                    >
                      <div className="flex items-center gap-1">
                        {col.label}
                        {sortColumn === col.key && (
                          sortDirection === "asc" ? (
                            <ArrowUpwardIcon style={{ fontSize: "14px" }} />
                          ) : (
                            <ArrowDownwardIcon style={{ fontSize: "14px" }} />
                          )
                        )}
                      </div>
                    </th>
                  ))}
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Document
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredCertificates.map((cert) => {
                  const IconComponent = getCertificateIcon(cert.type);
                  const typeColor = getCertificateTypeColor(cert.type);
                  const expirationStatus = getExpirationStatus(
                    cert.expiration_date,
                    cert.isMissing,
                    cert
                  );

                  return (
                    <tr
                      key={cert.uuid}
                      className="hover:bg-gray-50 cursor-pointer transition-colors duration-150"
                      onClick={() => handleRowClick(cert)}
                    >
                      {/* Employee */}
                      <td className="px-6 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-3">
                          <div className="w-9 h-9 bg-violet-100 rounded-full flex items-center justify-center">
                            <span className="text-xs font-semibold text-violet-600">
                              {getInitials(
                                cert.employee_first_name,
                                cert.employee_last_name
                              )}
                            </span>
                          </div>
                          <span className="text-sm font-medium text-gray-900">
                            {formatName(
                              [cert.employee_first_name, cert.employee_last_name]
                                .filter(Boolean)
                                .join(" ")
                            )}
                          </span>
                        </div>
                      </td>
                      {/* Type */}
                      <td className="px-6 py-2.5 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div
                            className={`w-8 h-8 ${typeColor.bgColor} rounded-lg flex items-center justify-center`}
                          >
                            <IconComponent
                              className={typeColor.iconColor}
                              style={{ fontSize: "18px" }}
                            />
                          </div>
                          <span className="text-sm text-gray-900">
                            {certificateTypeLookup[cert.type] || cert.type}
                          </span>
                        </div>
                      </td>
                      {/* Expiration Date */}
                      <td className="px-6 py-2.5 whitespace-nowrap">
                        <span className="text-sm text-gray-900">
                          {formatExpirationDate(cert.expiration_date)}
                        </span>
                      </td>
                      {/* Status */}
                      <td className="px-6 py-2.5 whitespace-nowrap">
                        <span
                          className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium ${expirationStatus.color} ${expirationStatus.bgColor}`}
                        >
                          {expirationStatus.label}
                        </span>
                      </td>
                      {/* Document thumbnail / Emergency contact info */}
                      <td className="px-6 py-2.5 whitespace-nowrap">
                        {cert.isEmergencyContact ? (
                          <div className="text-sm">
                            <p className="text-gray-900 font-medium">{cert.emergency_contact_name}</p>
                            {cert.emergency_contact_phone && (
                              <p className="text-gray-500 text-xs">{cert.emergency_contact_phone}</p>
                            )}
                          </div>
                        ) : cert.file ? (
                          <div
                            className="w-12 h-12 rounded border border-slate-200 overflow-hidden flex-shrink-0 cursor-pointer hover:opacity-80 transition-opacity"
                            onClick={(e) => handleThumbnailClick(e, cert.file)}
                          >
                            {isPdf(cert.file) ? (
                              <div className="w-full h-full flex items-center justify-center bg-red-50">
                                <PictureAsPdfIcon
                                  sx={{ fontSize: 20, color: "#ef4444" }}
                                />
                              </div>
                            ) : (
                              <img
                                src={cert.file}
                                alt=""
                                className="w-full h-full object-cover"
                              />
                            )}
                          </div>
                        ) : (
                          <div className="w-12 h-12 rounded border border-slate-200 overflow-hidden flex-shrink-0">
                            <div className="w-full h-full flex items-center justify-center bg-slate-50">
                              <ImageIcon sx={{ fontSize: 16, color: "#94a3b8" }} />
                            </div>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-8 text-center">
            <BadgeIcon
              className="text-gray-300 mx-auto mb-4"
              style={{ fontSize: "48px" }}
            />
            <p className="text-gray-600">
              {certificates.length === 0
                ? "No certificates found for this store."
                : "No certificates match your filters."}
            </p>
            <p className="text-gray-400 text-sm mt-2">
              {certificates.length === 0
                ? "Certificates will appear here when added to employee profiles."
                : "Try adjusting your search or filter criteria."}
            </p>
          </div>
        )}
      </div>

      <CertificateDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        certificateData={drawerData}
        isEdit={drawerIsEdit}
        onSave={handleDrawerSave}
        onDelete={drawerIsEdit ? handleDrawerDelete : undefined}
        employees={props.employees}
        certificateTypes={certificateTypes}
      />

      <ManageCertificateTypesModal
        open={manageTypesOpen}
        onClose={() => setManageTypesOpen(false)}
        homeStore={props.homeStore}
        certificateTypes={certificateTypes}
        systemTypes={allSystemTypes}
        requiredSlugs={requiredCertificateSlugs}
        roleNames={props.roleNames}
        onChanged={handleCatalogChanged}
        onVisibilityChanged={fetchCertificateTypes}
        onRequirementsChanged={handleRequirementsChanged}
      />

      {/* Emergency Contact Drawer */}
      <Drawer anchor="right" open={ecDrawerOpen} onClose={() => setEcDrawerOpen(false)}>
        <div className="w-[450px] h-full bg-white relative">
          <div className="max-h-screen overflow-auto pb-[150px]">
            <DrawerHeader
              title="Edit Emergency Contact"
              onClose={() => setEcDrawerOpen(false)}
            />
            <div className="px-6">
              <p className="text-sm text-slate-500 mb-4">
                {formatName(ecDrawerData.employee_name)}
              </p>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Contact Name</p>
                <div className="mt-1">
                  <TextField
                    placeholder="Full name"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="emergency_contact_name"
                    value={ecDrawerData.emergency_contact_name}
                    onChange={handleEcChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Contact Phone</p>
                <div className="mt-1">
                  <TextField
                    placeholder="Phone number"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="emergency_contact_phone"
                    value={ecDrawerData.emergency_contact_phone}
                    onChange={handleEcChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
              <div className="mt-4">
                <p className="text-sm text-slate-500">Relationship</p>
                <div className="mt-1">
                  <TextField
                    placeholder="e.g. Spouse, Parent, Sibling"
                    fullWidth
                    variant="outlined"
                    size="small"
                    name="emergency_contact_relationship"
                    value={ecDrawerData.emergency_contact_relationship}
                    onChange={handleEcChange}
                    InputProps={{
                      sx: {
                        backgroundColor: "white",
                        borderRadius: "8px",
                        padding: "5px",
                        height: "45px",
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#6d28d9",
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
          <DrawerFooterContainerLarge>
            <div className="w-full">
              <div className="w-full">
                <LoadingButton
                  variant="contained"
                  loading={ecSaving}
                  loadingPosition="end"
                  endIcon={<SaveIcon />}
                  onClick={handleEcSave}
                  sx={{
                    width: "100%",
                    textTransform: "none",
                    borderRadius: "12px",
                    paddingY: "14px",
                    backgroundColor: "#6d28d9",
                    "&:hover": { backgroundColor: "#5b21b6" },
                    fontSize: "16px",
                  }}
                >
                  Save
                </LoadingButton>
              </div>
              <div className="w-full mt-3">
                <ButtonOutlined text="Cancel" onClick={() => setEcDrawerOpen(false)} />
              </div>
            </div>
          </DrawerFooterContainerLarge>
        </div>
      </Drawer>
    </div>
  );
}

export default CertificationsTable;
